import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

declare global {
  interface ImportMeta {
    env?: {
      VITE_API_BASE_URL?: string;
    };
  }
}

type AttendanceStatus = 'PRESENT' | 'ABSENT' | 'LATE';

interface Lead {
  id: string;
  name: string;
  user?: Record<string, unknown>;
}

interface Group {
  id: string;
  name: string;
  allowFinance?: boolean;
  leadId?: string;
  role?: string;
}

interface Participant {
  id: string;
  name: string;
  phone?: string;
}

interface AttendanceInput {
  participantId: string;
  status: AttendanceStatus;
  notes?: string;
  participationScore?: number;
}

interface MeetingLocation {
  address?: string;
  coordinates?: {
    latitude: number;
    longitude: number;
  };
  isVirtual?: boolean;
  meetingUrl?: string;
}

interface AttendanceRecord {
  date: string;
  generalNotes: string;
  attendances: AttendanceInput[];
  createdAt: string;
}

interface GroupEvent {
  id: string;
  title: string;
  startTime: string;
  endTime: string;
  status?: string;
  hasAttendanceReport?: boolean;
  description?: string;
  location?: MeetingLocation;
}

interface GroupInsights {
  totalEvents: number;
  attendanceRate: number;
  presentCount: number;
  absentCount: number;
  lateCount: number;
  membersAnalyzed: number;
  byParticipant: Array<{
    participantId: string;
    name: string;
    attendanceRate: number;
    present: number;
    absent: number;
    late: number;
    excused: number;
  }>;
  byEvent: Array<{
    eventId: string;
    title: string;
    date?: string;
    attendedCount: number;
    totalCount: number;
    attendanceRate: number;
  }>;
}

interface ParticipantInsights {
  participant: {
    id: string;
    name: string;
    email?: string | null;
    phone?: string | null;
  };
  period: {
    startDate: string;
    endDate: string;
  };
  summary: {
    expectedEvents: number;
    attendedEvents: number;
    present: number;
    late: number;
    absent: number;
    excused: number;
    attendanceRate: number;
    consistencyScore: number;
    participationAverage: number;
    decliningParticipation: boolean;
    lastSeenAt?: string | null;
  };
  recentAttendances: Array<{
    eventId: string;
    eventTitle: string;
    eventDate?: string | null;
    status: string;
    notes?: string;
    markedAt?: string | null;
  }>;
}

interface LeadAttendanceContextValue {
  apiUrl: string;
  authenticatedFetch: (url: string, init?: RequestInit, tokenOverride?: string) => Promise<Response>;
  isBootstrapping: boolean;
  isAuthenticating: boolean;
  isLoadingParticipants: boolean;
  sessionToken: string | null;
  lead: Lead | null;
  groups: Group[];
  selectedGroupId: string | null;
  selectedGroup: Group | null;
  events: GroupEvent[];
  selectedEventId: string | null;
  selectedEvent: GroupEvent | null;
  isLoadingEvents: boolean;
  hasMoreEvents: boolean;
  eventsSearch: string;
  eventsStatusFilter: string;
  groupInsights: GroupInsights | null;
  isLoadingInsights: boolean;
  participants: Participant[];
  authLead: (leadCode: string) => Promise<void>;
  selectGroup: (groupId: string) => Promise<void>;
  selectEvent: (eventId: string | null) => void;
  createEvent: (payload: {
    title: string;
    startTime: string;
    endTime: string;
    description?: string;
    location?: MeetingLocation;
  }) => Promise<GroupEvent>;
  loadMoreEvents: () => Promise<void>;
  setEventFilters: (search: string, status?: string) => Promise<void>;
  refreshGroupInsights: () => Promise<void>;
  getParticipantInsights: (participantId: string) => Promise<ParticipantInsights>;
  clearSelectedGroup: () => void;
  refreshParticipants: () => Promise<void>;
  recordAttendance: (payload: {
    eventId?: string;
    date: string;
    generalNotes: string;
    location?: MeetingLocation;
    attendances: AttendanceInput[];
  }) => Promise<void>;
  getGroupHistory: (groupId?: string | null) => AttendanceRecord[];
  logout: () => void;
}

const API_URL = (import.meta.env?.VITE_API_BASE_URL || 'http://localhost:3001').replace(/\/$/, '');

console.log('API_URL:', import.meta.env?.VITE_API_BASE_URL, '->', API_URL);

const STORAGE_KEYS = {
  sessionToken: 'lead_attendance_session_token',
  refreshToken: 'lead_attendance_refresh_token',
  lead: 'lead_attendance_lead',
  groups: 'lead_attendance_groups',
  selectedGroupId: 'lead_attendance_selected_group_id',
  reportsPrefix: 'lead_attendance_reports_',
};

const LeadAttendanceContext = createContext<LeadAttendanceContextValue | null>(null);

const parseStoredJson = <T,>(value: string | null, fallback: T): T => {
  if (!value) return fallback;

  try {
    return JSON.parse(value) as T;
  } catch (_error) {
    return fallback;
  }
};

const normalizeEntityId = (value: unknown): string => {
  if (!value) return '';

  if (typeof value === 'string' || typeof value === 'number') {
    return String(value);
  }

  if (typeof value === 'object') {
    const candidate = value as Record<string, unknown>;
    return normalizeEntityId(candidate.$oid || candidate.id || candidate._id);
  }

  return '';
};

const normalizeGroup = (group: any): Group => ({
  id: String(group?.id || group?._id || ''),
  name: String(group?.name || 'Group'),
  allowFinance: Boolean(group?.allowFinance),
  leadId: group?.leadId ? String(group.leadId) : undefined,
  role: group?.role ? String(group.role) : undefined,
});

export function LeadAttendanceProvider({ children }: { children: React.ReactNode }) {
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [isLoadingParticipants, setIsLoadingParticipants] = useState(false);

  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [lead, setLead] = useState<Lead | null>(null);
  const [groups, setGroups] = useState<Group[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [events, setEvents] = useState<GroupEvent[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);
  const [hasMoreEvents, setHasMoreEvents] = useState(false);
  const [eventsPage, setEventsPage] = useState(1);
  const [eventsSearch, setEventsSearch] = useState('');
  const [eventsStatusFilter, setEventsStatusFilter] = useState('');
  const [groupInsights, setGroupInsights] = useState<GroupInsights | null>(null);
  const [isLoadingInsights, setIsLoadingInsights] = useState(false);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [participantStatsSupported, setParticipantStatsSupported] = useState<boolean | null>(null);
  const [groupScopedReportsSupported, setGroupScopedReportsSupported] = useState<boolean | null>(null);
  const [legacyReportsSupported, setLegacyReportsSupported] = useState<boolean | null>(null);

  const EVENTS_PAGE_SIZE = 20;

  const selectedGroup = useMemo(
    () => groups.find((group) => String(group.id) === String(selectedGroupId)) || null,
    [groups, selectedGroupId]
  );

  const selectedEvent = useMemo(
    () => events.find((event) => String(event.id) === String(selectedEventId)) || null,
    [events, selectedEventId]
  );

  const getReportKey = useCallback(
    (groupId?: string | null) => `${STORAGE_KEYS.reportsPrefix}${groupId || selectedGroupId || ''}`,
    [selectedGroupId]
  );

  const getGroupHistory = useCallback(
    (groupId?: string | null) => {
      const key = getReportKey(groupId);
      return parseStoredJson<AttendanceRecord[]>(localStorage.getItem(key), []);
    },
    [getReportKey]
  );

  const saveGroupHistory = useCallback(
    (record: AttendanceRecord, groupId?: string | null) => {
      const key = getReportKey(groupId);
      const current = getGroupHistory(groupId);
      const next = [record, ...current].slice(0, 250);
      localStorage.setItem(key, JSON.stringify(next));
    },
    [getGroupHistory, getReportKey]
  );

  const clearSession = useCallback(() => {
    localStorage.removeItem(STORAGE_KEYS.sessionToken);
    localStorage.removeItem(STORAGE_KEYS.refreshToken);
    localStorage.removeItem(STORAGE_KEYS.lead);
    localStorage.removeItem(STORAGE_KEYS.groups);
    localStorage.removeItem(STORAGE_KEYS.selectedGroupId);
  }, []);

  const resetToAuthOnInvalidGroup = useCallback(() => {
    clearSession();
    setSessionToken(null);
    setRefreshToken(null);
    setLead(null);
    setGroups([]);
    setSelectedGroupId(null);
    setSelectedEventId(null);
    setParticipants([]);
    setEvents([]);
    setGroupInsights(null);
    setGroupScopedReportsSupported(null);
    setLegacyReportsSupported(null);
  }, [clearSession]);

  const refreshAccessToken = useCallback(async (): Promise<string> => {
    const storedRefreshToken = refreshToken || localStorage.getItem(STORAGE_KEYS.refreshToken);

    if (!storedRefreshToken) {
      throw new Error('Session expired. Please sign in again.');
    }

    const response = await fetch(`${API_URL}/chatbot/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refreshToken: storedRefreshToken }),
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok || !data.sessionToken) {
      throw new Error(data.message || 'Could not refresh session.');
    }

    const nextSessionToken = String(data.sessionToken);
    const nextRefreshToken = data.refreshToken ? String(data.refreshToken) : storedRefreshToken;

    setSessionToken(nextSessionToken);
    setRefreshToken(nextRefreshToken);
    localStorage.setItem(STORAGE_KEYS.sessionToken, nextSessionToken);
    localStorage.setItem(STORAGE_KEYS.refreshToken, nextRefreshToken);

    return nextSessionToken;
  }, [refreshToken]);

  const authenticatedFetch = useCallback(async (
    url: string,
    init: RequestInit = {},
    tokenOverride?: string,
  ) => {
    const token = tokenOverride || sessionToken || localStorage.getItem(STORAGE_KEYS.sessionToken);
    const headers = new Headers(init.headers || {});

    if (token) {
      headers.set('x-session-token', token);
    }

    const response = await fetch(url, {
      ...init,
      headers,
    });

    if (response.ok) {
      return response;
    }

    const data = await response.clone().json().catch(() => ({}));
    const message = String(data?.message || '').toLowerCase();
    const shouldRefresh = response.status === 401 || message.includes('session');

    if (!shouldRefresh) {
      return response;
    }

    try {
      const nextToken = await refreshAccessToken();
      const retryHeaders = new Headers(init.headers || {});
      retryHeaders.set('x-session-token', nextToken);

      return await fetch(url, {
        ...init,
        headers: retryHeaders,
      });
    } catch (_error) {
      clearSession();
      setSessionToken(null);
      setRefreshToken(null);
      setLead(null);
      setGroups([]);
      setSelectedGroupId(null);
      setParticipants([]);
      throw new Error('Session expired. Please sign in again.');
    }
  }, [clearSession, refreshAccessToken, sessionToken]);

  const fetchParticipants = useCallback(
    async (groupId: string, tokenOverride?: string) => {
      setIsLoadingParticipants(true);

      try {
        let response = await authenticatedFetch(
          `${API_URL}/chatbot/groups/${groupId}/participants`,
          {
            method: 'GET',
          },
          tokenOverride,
        );

        // Backward compatibility for environments still exposing the legacy route.
        if (response.status === 404) {
          response = await authenticatedFetch(
            `${API_URL}/chatbot/group/participants`,
            {
              method: 'GET',
            },
            tokenOverride,
          );
        }

        const data = await response.json().catch(() => ({}));

        if (response.status === 404) {
          const notFoundMessage = String(data?.message || '').toLowerCase();
          if (notFoundMessage.includes('group not found')) {
            resetToAuthOnInvalidGroup();
            throw new Error('Group not found. Please sign in again.');
          }
        }

        if (!response.ok) {
          throw new Error(data.message || `Failed to fetch participants (${response.status})`);
        }

        setParticipants(Array.isArray(data.participants) ? data.participants : []);
      } finally {
        setIsLoadingParticipants(false);
      }
    },
    [authenticatedFetch, resetToAuthOnInvalidGroup]
  );

  const fetchGroupSummary = useCallback(
    async (groupId: string, tokenOverride?: string) => {
      const response = await authenticatedFetch(
        `${API_URL}/chatbot/groups/${groupId}`,
        {
          method: 'GET',
        },
        tokenOverride,
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.message || `Failed to fetch group summary (${response.status})`);
      }

      const normalized = normalizeGroup(data);

      // Fallback capability probe: some environments may return a stale/missing
      // allowFinance flag while finance endpoints are already active for the group.
      if (!normalized.allowFinance) {
        try {
          const financeProbe = await authenticatedFetch(
            `${API_URL}/chatbot/groups/${groupId}/finance/analytics`,
            { method: 'GET' },
            tokenOverride,
          );

          if (financeProbe.ok) {
            normalized.allowFinance = true;
          }
        } catch (_error) {
          // Keep original flag when probe fails.
        }
      }

      setGroups((prev) => {
        const next = prev.map((group) =>
          String(group.id) === String(normalized.id)
            ? { ...group, ...normalized }
            : group,
        );

        localStorage.setItem(STORAGE_KEYS.groups, JSON.stringify(next));
        return next;
      });

      return normalized;
    },
    [authenticatedFetch],
  );

  const fetchEventsPage = useCallback(
    async (
      groupId: string,
      page: number,
      options?: {
        append?: boolean;
        search?: string;
        status?: string;
      },
      tokenOverride?: string,
    ) => {
      const append = Boolean(options?.append);
      const search = (options?.search ?? '').trim();
      const status = (options?.status ?? '').trim();

      setIsLoadingEvents(true);

      try {
        const params = new URLSearchParams();
        params.set('page', String(page));
        params.set('limit', String(EVENTS_PAGE_SIZE));
        if (search) params.set('search', search);
        if (status) params.set('status', status);

        const response = await authenticatedFetch(
          `${API_URL}/chatbot/groups/${groupId}/events/upcoming?${params.toString()}`,
          {
            method: 'GET',
          },
          tokenOverride,
        );

        const data = await response.json().catch(() => ([]));

        if (response.status === 404) {
          const notFoundMessage = String((data as any)?.message || '').toLowerCase();
          if (notFoundMessage.includes('group not found')) {
            resetToAuthOnInvalidGroup();
            throw new Error('Group not found. Please sign in again.');
          }
        }

        if (!response.ok) {
          throw new Error(
            (data && data.message) || `Failed to fetch events (${response.status})`,
          );
        }

        const items = Array.isArray(data)
          ? data
          : Array.isArray(data.items)
            ? data.items
            : [];

          const normalizedEvents = items
            .filter((event: any) => event && (event.id || event._id))
            .map((event: any) => ({
                id: String(event.id || event._id),
                title: String(event.title || 'Untitled Ceremony'),
                startTime: String(event.startTime || event.createdAt || new Date().toISOString()),
                endTime: String(event.endTime || event.startTime || event.createdAt || new Date().toISOString()),
                status: event.status ? String(event.status) : undefined,
                hasAttendanceReport: Boolean(event.hasAttendanceReport),
                description: event.description ? String(event.description) : undefined,
              }));

        let addedCount = normalizedEvents.length;
        setEvents((prev) => {
          if (!append) return normalizedEvents;

          const map = new Map<string, GroupEvent>();
          prev.forEach((event) => map.set(String(event.id), event));

          let localAdded = 0;
          normalizedEvents.forEach((event: GroupEvent) => {
            const key = String(event.id);
            if (!map.has(key)) {
              localAdded += 1;
            }
            map.set(key, event);
          });

          addedCount = localAdded;
          return Array.from(map.values());
        });

        const hasMoreByPayload = Array.isArray(data)
          ? normalizedEvents.length === EVENTS_PAGE_SIZE
          : Boolean(data.hasMore);

        const hasMore = append && normalizedEvents.length > 0 && addedCount === 0
          ? false
          : hasMoreByPayload;

        setEventsPage(page);
        setHasMoreEvents(hasMore);
      } catch (error) {
        if (append) {
          // Stop requesting more pages repeatedly if a trailing-page request fails.
          setHasMoreEvents(false);
        }
        throw error;
      } finally {
        setIsLoadingEvents(false);
      }
    },
    [authenticatedFetch, resetToAuthOnInvalidGroup],
  );

  const fetchGroupInsights = useCallback(async (groupId: string, tokenOverride?: string) => {
    setIsLoadingInsights(true);

    try {
      let response: Response | null = null;

      if (groupScopedReportsSupported !== false) {
        response = await authenticatedFetch(
          `${API_URL}/chatbot/groups/${groupId}/reports/attendance`,
          { method: 'GET' },
          tokenOverride,
        );

        if (response.status === 404) {
          setGroupScopedReportsSupported(false);
          response = null;
        } else if (response.ok) {
          setGroupScopedReportsSupported(true);
        }
      }

      if (!response && legacyReportsSupported !== false) {
        response = await authenticatedFetch(
          `${API_URL}/chatbot/reports/attendance`,
          { method: 'GET' },
          tokenOverride,
        );

        if (response.status === 404) {
          setLegacyReportsSupported(false);
          response = null;
        } else if (response.ok) {
          setLegacyReportsSupported(true);
        }
      }

      if (!response) {
        setGroupInsights(null);
        return;
      }

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        if (response.status === 404) {
          const notFoundMessage = String(data?.message || '').toLowerCase();
          if (notFoundMessage.includes('group not found')) {
            resetToAuthOnInvalidGroup();
            return;
          }
        }

        setGroupInsights(null);
        return;
      }

      const payload = data?.data || data;
      const overview = payload?.overview || {};
      const byParticipant = Array.isArray(payload?.byParticipant)
        ? payload.byParticipant
        : [];
      const byEvent = Array.isArray(payload?.byEvent)
        ? payload.byEvent
        : [];

      setGroupInsights({
        totalEvents: Number(payload?.totalEvents || 0),
        attendanceRate: Number(overview?.attendanceRate || 0),
        presentCount: Number(overview?.presentCount || 0),
        absentCount: Number(overview?.absentCount || 0),
        lateCount: Number(overview?.lateCount || 0),
        membersAnalyzed: byParticipant.length,
        byParticipant,
        byEvent: byEvent.map((event: any) => ({
          eventId: normalizeEntityId(
            event?.eventId || event?.id || event?._id || event?.event?.id || event?.event?._id,
          ),
          title: String(event?.title || event?.eventTitle || event?.event?.title || 'Ceremony'),
          date: event?.date
            ? String(event.date)
            : event?.startTime
              ? String(event.startTime)
              : undefined,
          attendedCount: Number(event?.attendedCount || 0),
          totalCount: Number(event?.totalCount || 0),
          attendanceRate: Number(event?.attendanceRate || 0),
        })),
      });
    } finally {
      setIsLoadingInsights(false);
    }
  }, [
    authenticatedFetch,
    groupScopedReportsSupported,
    legacyReportsSupported,
    resetToAuthOnInvalidGroup,
  ]);

  useEffect(() => {
    const token = localStorage.getItem(STORAGE_KEYS.sessionToken);
    const storedRefreshToken = localStorage.getItem(STORAGE_KEYS.refreshToken);
    const storedLead = parseStoredJson<Lead | null>(localStorage.getItem(STORAGE_KEYS.lead), null);
    const storedGroups = parseStoredJson<Group[]>(localStorage.getItem(STORAGE_KEYS.groups), []).map(normalizeGroup);
    const storedGroupId = localStorage.getItem(STORAGE_KEYS.selectedGroupId);

    if (!token || !storedRefreshToken || !storedLead || storedGroups.length === 0) {
      setIsBootstrapping(false);
      return;
    }

    setSessionToken(token);
    setRefreshToken(storedRefreshToken);
    setLead(storedLead);
    setGroups(storedGroups);

    const canReuseGroup = storedGroups.some((group) => String(group.id) === String(storedGroupId));
    if (canReuseGroup && storedGroupId) {
      setSelectedGroupId(storedGroupId);
      Promise.all([
        fetchGroupSummary(storedGroupId, token).catch(() => null),
        fetchParticipants(storedGroupId, token),
        fetchEventsPage(
          storedGroupId,
          1,
          { append: false, search: '', status: '' },
          token,
        ),
        fetchGroupInsights(storedGroupId, token),
      ]).catch(() => {
        setParticipants([]);
        setEvents([]);
        setGroupInsights(null);
      });
    } else if (storedGroups.length === 1) {
      const autoGroup = String(storedGroups[0].id);
      setSelectedGroupId(autoGroup);
      localStorage.setItem(STORAGE_KEYS.selectedGroupId, autoGroup);
      Promise.all([
        fetchGroupSummary(autoGroup, token).catch(() => null),
        fetchParticipants(autoGroup, token),
        fetchEventsPage(
          autoGroup,
          1,
          { append: false, search: '', status: '' },
          token,
        ),
        fetchGroupInsights(autoGroup, token),
      ]).catch(() => {
        setParticipants([]);
        setEvents([]);
        setGroupInsights(null);
      });
    }

    setIsBootstrapping(false);
  }, [fetchEventsPage, fetchGroupInsights, fetchGroupSummary, fetchParticipants]);

  const authLead = useCallback(async (leadCode: string) => {
    setIsAuthenticating(true);

    try {
      const response = await fetch(`${API_URL}/chatbot/auth`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ leadCode }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.message || 'Authentication failed.');
      }

      if (!data.sessionToken || !data.refreshToken || !data.lead || !Array.isArray(data.groups) || data.groups.length === 0) {
        throw new Error('Invalid authentication response.');
      }

      const token = data.sessionToken as string;
      const nextRefreshToken = data.refreshToken as string;
      const authenticatedLead = data.lead as Lead;
      const leadGroups = Array.isArray(data.groups)
        ? (data.groups as Group[]).map(normalizeGroup)
        : [];

      setSessionToken(token);
      setRefreshToken(nextRefreshToken);
      setLead(authenticatedLead);
      setGroups(leadGroups);
      setParticipants([]);
      setEvents([]);
      setGroupInsights(null);

      localStorage.setItem(STORAGE_KEYS.sessionToken, token);
      localStorage.setItem(STORAGE_KEYS.refreshToken, nextRefreshToken);
      localStorage.setItem(STORAGE_KEYS.lead, JSON.stringify(authenticatedLead));
      localStorage.setItem(STORAGE_KEYS.groups, JSON.stringify(leadGroups));

      if (leadGroups.length === 1) {
        const autoGroup = String(leadGroups[0].id);
        setSelectedGroupId(autoGroup);
        localStorage.setItem(STORAGE_KEYS.selectedGroupId, autoGroup);
        await Promise.all([
          fetchGroupSummary(autoGroup, token).catch(() => null),
          fetchParticipants(autoGroup, token),
          fetchEventsPage(
            autoGroup,
            1,
            { append: false, search: '', status: '' },
            token,
          ),
          fetchGroupInsights(autoGroup, token),
        ]);
      } else {
        setSelectedGroupId(null);
        setSelectedEventId(null);
        localStorage.removeItem(STORAGE_KEYS.selectedGroupId);
      }
    } finally {
      setIsAuthenticating(false);
    }
  }, [fetchEventsPage, fetchGroupInsights, fetchGroupSummary, fetchParticipants]);

  const selectGroup = useCallback(async (groupId: string) => {
    if (!sessionToken) {
      throw new Error('Session missing. Please sign in again.');
    }

    const normalizedGroupId = String(groupId);
    setSelectedGroupId(normalizedGroupId);
    setSelectedEventId(null);
    localStorage.setItem(STORAGE_KEYS.selectedGroupId, normalizedGroupId);
    await Promise.all([
      fetchGroupSummary(normalizedGroupId, sessionToken).catch(() => null),
      fetchParticipants(normalizedGroupId, sessionToken),
      fetchEventsPage(
        normalizedGroupId,
        1,
        { append: false, search: eventsSearch, status: eventsStatusFilter },
        sessionToken,
      ),
      fetchGroupInsights(normalizedGroupId, sessionToken),
    ]);
  }, [eventsSearch, eventsStatusFilter, fetchEventsPage, fetchGroupInsights, fetchGroupSummary, fetchParticipants, sessionToken]);

  const clearSelectedGroup = useCallback(() => {
    setSelectedGroupId(null);
    setSelectedEventId(null);
    setParticipants([]);
    setEvents([]);
    setGroupInsights(null);
    localStorage.removeItem(STORAGE_KEYS.selectedGroupId);
  }, []);

  const refreshParticipants = useCallback(async () => {
    if (!sessionToken || !selectedGroupId) return;
    await fetchParticipants(selectedGroupId, sessionToken);
  }, [fetchParticipants, selectedGroupId, sessionToken]);

  const recordAttendance = useCallback(async (payload: {
    eventId?: string;
    date: string;
    generalNotes: string;
    location?: MeetingLocation;
    attendances: AttendanceInput[];
  }) => {
    if (!sessionToken || !selectedGroupId) {
      throw new Error('Session missing. Please sign in again.');
    }

    
    let url = `${API_URL}/chatbot/groups/${selectedGroupId}/attendance/record`;
    if (selectedEventId) {
       url = `${API_URL}/chatbot/events/${selectedEventId}/attendance/mark`;
    } else if (payload.eventId) {
       url = `${API_URL}/chatbot/events/${payload.eventId}/attendance/mark`;
    }

    const response = await authenticatedFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      
      // map to what markAttendance expects on new endpoint if event exists
      body: JSON.stringify(selectedEventId || payload.eventId ? {
          attendances: payload.attendances,
          meetingNotes: payload.generalNotes,
          location: payload.location,
      } : payload),
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      if (response.status === 401) {
        clearSession();
        setSessionToken(null);
        setLead(null);
        setGroups([]);
        setSelectedGroupId(null);
        setParticipants([]);
      }

      throw new Error(data.message || `Failed to record attendance (${response.status}).`);
    }

    saveGroupHistory(
      {
        ...payload,
        createdAt: new Date().toISOString(),
      },
      selectedGroupId
    );

    // Keep event badges and summary cards in sync right after attendance submission.
    await Promise.all([
      fetchGroupInsights(selectedGroupId, sessionToken),
      fetchEventsPage(
        selectedGroupId,
        1,
        {
          append: false,
          search: eventsSearch,
          status: eventsStatusFilter,
        },
        sessionToken,
      ),
    ]);
  }, [
    authenticatedFetch,
    clearSession,
    saveGroupHistory,
    selectedEventId,
    selectedGroupId,
    sessionToken,
    fetchGroupInsights,
    fetchEventsPage,
    eventsSearch,
    eventsStatusFilter,
  ]);

  const logout = useCallback(() => {
    clearSession();
    setSessionToken(null);
    setRefreshToken(null);
    setLead(null);
    setGroups([]);
    setSelectedGroupId(null);
    setSelectedEventId(null);
    setParticipants([]);
    setEvents([]);
    setGroupInsights(null);
    setGroupScopedReportsSupported(null);
    setLegacyReportsSupported(null);
  }, [clearSession]);

  const selectEvent = useCallback((eventId: string | null) => {
    setSelectedEventId(eventId);
  }, []);

  const createEvent = useCallback(async (payload: {
    title: string;
    startTime: string;
    endTime: string;
    description?: string;
    location?: MeetingLocation;
  }): Promise<GroupEvent> => {
    if (!sessionToken || !selectedGroupId) {
      throw new Error('Session missing. Please sign in again.');
    }

    const response = await authenticatedFetch(`${API_URL}/chatbot/groups/${selectedGroupId}/events`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(data.message || `Failed to create event (${response.status}).`);
    }

    const createdEvent: GroupEvent = {
      id: String(data.id || data._id),
      title: String(data.title || payload.title),
      startTime: String(data.startTime || payload.startTime),
      endTime: String(data.endTime || payload.endTime),
      status: data.status ? String(data.status) : 'SCHEDULED',
      hasAttendanceReport: Boolean(data.hasAttendanceReport),
      description: data.description ? String(data.description) : payload.description,
      location: data.location || payload.location,
    };

    setEvents(prev => [createdEvent, ...prev.filter((event) => String(event.id) !== createdEvent.id)]);
    setSelectedEventId(createdEvent.id);

    return createdEvent;
  }, [authenticatedFetch, fetchGroupInsights, selectedGroupId, sessionToken]);

  const loadMoreEvents = useCallback(async () => {
    if (!selectedGroupId || isLoadingEvents || !hasMoreEvents) return;

    await fetchEventsPage(selectedGroupId, eventsPage + 1, {
      append: true,
      search: eventsSearch,
      status: eventsStatusFilter,
    });
  }, [eventsPage, eventsSearch, eventsStatusFilter, fetchEventsPage, hasMoreEvents, isLoadingEvents, selectedGroupId]);

  const setEventFilters = useCallback(async (search: string, status: string = '') => {
    if (!selectedGroupId) return;

    setEventsSearch(search);
    setEventsStatusFilter(status);
    setSelectedEventId(null);

    await fetchEventsPage(
      selectedGroupId,
      1,
      {
        append: false,
        search,
        status,
      },
    );
  }, [fetchEventsPage, selectedGroupId]);

  const refreshGroupInsights = useCallback(async () => {
    if (!selectedGroupId) return;
    await fetchGroupInsights(selectedGroupId);
  }, [fetchGroupInsights, selectedGroupId]);

  const getParticipantInsights = useCallback(async (participantId: string): Promise<ParticipantInsights> => {
    if (!selectedGroupId) {
      throw new Error('Select a group first.');
    }

    const buildFallbackInsights = (): ParticipantInsights => {
      const participant = participants.find(
        (item) => String(item.id) === String(participantId),
      );

      const end = new Date();
      const start = new Date(end.getTime() - 180 * 24 * 60 * 60 * 1000);

      return {
        participant: {
          id: participant?.id || participantId,
          name: participant?.name || 'Membro',
          email: null,
          phone: participant?.phone || null,
        },
        period: {
          startDate: start.toISOString(),
          endDate: end.toISOString(),
        },
        summary: {
          expectedEvents: 0,
          attendedEvents: 0,
          present: 0,
          late: 0,
          absent: 0,
          excused: 0,
          attendanceRate: 0,
          consistencyScore: 0,
          participationAverage: 0,
          decliningParticipation: false,
          lastSeenAt: null,
        },
        recentAttendances: [],
      };
    };

    if (participantStatsSupported === false) {
      return buildFallbackInsights();
    }

    const response = await authenticatedFetch(
      `${API_URL}/chatbot/groups/${selectedGroupId}/participants/${participantId}/stats`,
      { method: 'GET' },
    );

    const data = await response.json().catch(() => ({}));

    if (response.status === 404) {
      setParticipantStatsSupported(false);
      return buildFallbackInsights();
    }

    if (!response.ok) {
      throw new Error(data.message || `Failed to fetch participant stats (${response.status})`);
    }

    if (participantStatsSupported !== true) {
      setParticipantStatsSupported(true);
    }

    return data as ParticipantInsights;
  }, [authenticatedFetch, participantStatsSupported, participants, selectedGroupId]);

  const value = useMemo<LeadAttendanceContextValue>(
    () => ({
      apiUrl: API_URL,
      authenticatedFetch,
      isBootstrapping,
      isAuthenticating,
      isLoadingParticipants,
      sessionToken,
      lead,
      groups,
      selectedGroupId,
      selectedGroup,
      participants,
      authLead,
      selectGroup,
      clearSelectedGroup,
      refreshParticipants,
      recordAttendance,
      getGroupHistory,
      logout,
      events,
      selectedEventId,
      selectedEvent,
      isLoadingEvents,
      hasMoreEvents,
      eventsSearch,
      eventsStatusFilter,
      groupInsights,
      isLoadingInsights,
      selectEvent,
      createEvent,
      loadMoreEvents,
      setEventFilters,
      refreshGroupInsights,
      getParticipantInsights,
    }),
    [
      authenticatedFetch,
      authLead,
      getGroupHistory,
      groups,
      isAuthenticating,
      isBootstrapping,
      isLoadingParticipants,
      lead,
      logout,
      participants,
      recordAttendance,
      refreshParticipants,
      selectGroup,
      clearSelectedGroup,
      selectedGroup,
      selectedGroupId,
      sessionToken,
      events,
      selectedEventId,
      selectedEvent,
      selectEvent,
      createEvent,
      isLoadingEvents,
      hasMoreEvents,
      eventsSearch,
      eventsStatusFilter,
      groupInsights,
      isLoadingInsights,
      loadMoreEvents,
      setEventFilters,
      refreshGroupInsights,
      getParticipantInsights,
    ]
  );

  return <LeadAttendanceContext.Provider value={value}>{children}</LeadAttendanceContext.Provider>;
}

export function useLeadAttendance() {
  const context = useContext(LeadAttendanceContext);

  if (!context) {
    throw new Error('useLeadAttendance must be used inside LeadAttendanceProvider');
  }

  return context;
}

export type {
  AttendanceInput,
  MeetingLocation,
  AttendanceRecord,
  AttendanceStatus,
  Group,
  GroupInsights,
  GroupEvent,
  Lead,
  Participant,
  ParticipantInsights,
};
